import type { ButtonVariants } from '@talos/kyoko';
import {
  canViewDeepDive,
  Divider,
  DropdownItem,
  DropdownMenu,
  FormControlSizes,
  IconButton,
  IconName,
  isMultileg,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Tooltip,
  useMixpanel,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback } from 'react';
import { useIsMobileLayout } from '../../hooks';
import type { MultilegComboType } from '../MultilegCombo/enums';
import { CloneSymbolDropdownItem } from '../SymbolActionsMenu/CloneSymbolDropdownItem';
import { CreateMultilegDropdownItem } from '../SymbolActionsMenu/CreateMultilegDropdownItem';
import { FavoriteSymbolDropdownItem } from '../SymbolActionsMenu/FavoriteSymbolDropdownItem';

interface MarketDataCardMenuProps {
  security: Security | undefined;
  onOpenDeepDive: () => void;
  mixpanelSource?: MixpanelEventSource;
  dataTestId?: string;
  usePortal?: boolean;
  onOpenSettings?: () => void;
  onDeleteCard?: () => void;
  onCreateMultileg?: (multilegType: MultilegComboType) => void;
  onEditSymbol?: () => void;
  onCloneSymbol?: (security: Security) => void;
  onOpenMarketSelector?: () => void;
  onOpenPublishCustomRate?: () => void;
  onOpenClearCustomRate?: () => void;
  canClearCustomRate?: boolean;
  triggerVariant?: ButtonVariants;
}

export const MarketDataCardMenu = memo(function MarketDataCardMenu({
  security,
  onOpenDeepDive,
  mixpanelSource = MixpanelEventSource.MarketCardHeader,
  dataTestId = 'market-data-card-menu',
  usePortal,
  onOpenSettings,
  onDeleteCard,
  onCreateMultileg,
  onEditSymbol,
  onCloneSymbol,
  onOpenMarketSelector,
  onOpenClearCustomRate,
  onOpenPublishCustomRate,
  canClearCustomRate,
  triggerVariant,
}: MarketDataCardMenuProps) {
  const isMultilegSecurity = isMultileg(security);
  const mixpanel = useMixpanel();
  const isMobileLayout = useIsMobileLayout();

  const handleMenuOpen = useCallback(() => {
    mixpanel.track(MixpanelEvent.OpenSymbolMenu, {
      [MixpanelEventProperty.Source]: mixpanelSource,
    });
  }, [mixpanel, mixpanelSource]);

  return (
    <DropdownMenu
      onOpen={handleMenuOpen}
      usePortal={usePortal}
      triggerElement={
        <IconButton
          data-testid={dataTestId}
          ghost={triggerVariant == null}
          variant={triggerVariant}
          size={FormControlSizes.Small}
          dim={triggerVariant == null}
          icon={IconName.DotsVertical}
        />
      }
      trigger="click"
      placement="bottom-end"
      dropdownProps={{ style: { width: '220px' } }}
    >
      {onOpenSettings ? (
        <DropdownItem startIcon={IconName.Cog} onClick={onOpenSettings}>
          Edit Card
        </DropdownItem>
      ) : null}

      {!isMultilegSecurity && onOpenMarketSelector ? (
        <DropdownItem startIcon={IconName.Scale} onClick={onOpenMarketSelector}>
          Market Selector
        </DropdownItem>
      ) : null}

      <FavoriteSymbolDropdownItem security={security} source={mixpanelSource} />

      {!isMobileLayout && canViewDeepDive(security) && (
        <DropdownItem startIcon={IconName.Deepdive} onClick={onOpenDeepDive}>
          Open DeepDive
        </DropdownItem>
      )}

      {onOpenPublishCustomRate ? (
        <DropdownItem
          startIcon={IconName.Pencil}
          submenuContent={
            <>
              <DropdownItem data-testid="custom-rate-publish" onClick={onOpenPublishCustomRate}>
                Publish
              </DropdownItem>
              <Tooltip
                tooltip="Select one internal market account on the market card to clear its custom rate."
                disabled={canClearCustomRate}
                placement="right"
              >
                <DropdownItem
                  data-testid="custom-rate-clear"
                  disabled={!canClearCustomRate}
                  onClick={onOpenClearCustomRate}
                >
                  Clear
                </DropdownItem>
              </Tooltip>
            </>
          }
        >
          Custom Rate
        </DropdownItem>
      ) : null}

      {onCreateMultileg ? <CreateMultilegDropdownItem security={security} onCreateMultileg={onCreateMultileg} /> : null}

      {!isMobileLayout && isMultilegSecurity && onEditSymbol && (
        <DropdownItem data-testid="edit-symbol-dropdown-item" startIcon={IconName.Pencil} onClick={onEditSymbol}>
          Edit Symbol
        </DropdownItem>
      )}
      {!isMobileLayout && <CloneSymbolDropdownItem security={security} onCloneSymbol={onCloneSymbol} />}

      {onDeleteCard ? (
        <>
          <Divider />
          <DropdownItem startIcon={IconName.Trash} onClick={onDeleteCard}>
            Delete Card
          </DropdownItem>
        </>
      ) : null}
    </DropdownMenu>
  );
});
